import { useState } from 'react';
import { Link } from 'react-router-dom';
import { KpiCard } from '@/shared/components/kpi-card';
import { LeadAvatar } from '@/shared/components/lead-avatar';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { useMgmLead } from '../api/use-mgm-admin';
import { formatRate } from '../model/referral-status';
import { ReferralStatusBadge } from './mgm-badges';
import { MgmReferralDialog } from './mgm-referral-dialog';
import { MgmReferralList } from './mgm-referral-list';
import { METRIC_ICON } from './status-icons';

/**
 * Aba "Indicações" no detalhe do lead (spec, seções 13 e 14): o lead como
 * canal de aquisição — quantos indicou, quantos viraram cadastro e compra,
 * quanto ganhou — e quem o indicou, se alguém indicou.
 */
export function LeadMgmTab({ idPassbook, leadId }: { idPassbook: string; leadId: string }) {
  const { data, isLoading } = useMgmLead(idPassbook, leadId);
  const [openId, setOpenId] = useState<string | null>(null);

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-48 rounded-2xl" />
      </div>
    );
  }

  const s = data.stats;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard index={0} icon={METRIC_ICON.indicated} label="Indicações" value={s.indicated} hint={data.code ? `Código ${data.code}` : 'Ainda não abriu o link'} />
        <KpiCard index={1} icon={METRIC_ICON.registered} label="Cadastros" value={s.registered} hint={`${formatRate(s.rates.registrationRate)} das indicações`} />
        <KpiCard index={2} icon={METRIC_ICON.converted} label="Conversões" value={s.converted} hint={`${formatRate(s.rates.conversionOverRegistered)} dos cadastros`} />
        <KpiCard index={3} icon={METRIC_ICON.rewards} label="Recompensas" value={s.rewardsGranted} hint={s.earnedText ? `Total ${s.earnedText}` : undefined} />
      </div>

      {data.referredBy ? (
        <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex min-w-0 items-center gap-3">
            <LeadAvatar
              lead={{
                _id: data.referredBy.referrer.userId,
                profile: {
                  name: data.referredBy.referrer.name || '?',
                  uniqueId: data.referredBy.referrer.uniqueId ?? undefined,
                },
                gender: data.referredBy.referrer.gender ?? undefined,
              }}
              className="size-10"
            />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Indicado por</p>
              <p className="truncate font-medium">
                {data.referredBy.referrer.name || '—'}{' '}
                <span className="font-mono text-xs text-muted-foreground">{data.referredBy.referrer.code}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            className="rounded-lg"
            onClick={() => setOpenId(data.referredBy ? data.referredBy.id : null)}
            aria-label="Ver indicação"
          >
            <ReferralStatusBadge status={data.referredBy.status} rejectReason={data.referredBy.rejectReason} />
          </button>
        </Card>
      ) : null}

      <MgmReferralList
        rows={data.referrals}
        idPassbook={idPassbook}
        onOpen={setOpenId}
        hideReferrer
        emptyDescription="Este cliente ainda não indicou ninguém."
      />
      {data.total > data.referrals.length && data.code ? (
        <p className="text-center text-xs text-muted-foreground">
          Mostrando as {data.referrals.length} mais recentes de {data.total}.{' '}
          <Link className="underline" to={`/loja/${idPassbook}/indique-e-ganhe`}>
            Ver todas no Indique e ganhe
          </Link>{' '}
          (filtre pelo código {data.code}).
        </p>
      ) : null}

      <MgmReferralDialog idPassbook={idPassbook} referralId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
